import { Form, Input, InputNumber, Select, Switch, Row, Col, Divider, Spin } from 'antd'
import { useQuery } from '@tanstack/react-query'
import { hospitalsApi } from '@/api/hospitals'

const { TextArea } = Input

// Predefined facility types
const FACILITY_TYPES = {
  HOSPITAL: 'Hospital',
  MOBILE_ASSESSMENT: 'mobile assessment team',
  OTHER: 'Other',
} as const

export const HospitalForm = () => {
  const form = Form.useFormInstance()

  // Fetch markets for dropdown
  const { data: marketsData, isLoading: marketsLoading } = useQuery({
    queryKey: ['markets'],
    queryFn: () => hospitalsApi.getMarkets(),
  })

  const markets = marketsData?.data || []
  // US States list for dropdown
  const usStates = [
    'AL',
    'AK',
    'AZ',
    'AR',
    'CA',
    'CO',
    'CT',
    'DE',
    'FL',
    'GA',
    'HI',
    'ID',
    'IL',
    'IN',
    'IA',
    'KS',
    'KY',
    'LA',
    'ME',
    'MD',
    'MA',
    'MI',
    'MN',
    'MS',
    'MO',
    'MT',
    'NE',
    'NV',
    'NH',
    'NJ',
    'NM',
    'NY',
    'NC',
    'ND',
    'OH',
    'OK',
    'OR',
    'PA',
    'RI',
    'SC',
    'SD',
    'TN',
    'TX',
    'UT',
    'VT',
    'VA',
    'WA',
    'WV',
    'WI',
    'WY',
  ]

  return (
    <>
      {/* Basic Information */}
      <Divider orientation="left">Basic Information</Divider>
      <Row gutter={16}>
        <Col span={12}>
          <Form.Item
            name="name"
            label="Hospital Name"
            rules={[
              { required: true, message: 'Hospital name is required' },
              { max: 255, message: 'Name must be less than 255 characters' },
            ]}
          >
            <Input placeholder="Enter hospital name" />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item
            name="cmsid"
            label="CMS ID"
            rules={[{ required: true, message: 'CMS ID is required' }]}
          >
            <InputNumber
              style={{ width: '100%' }}
              placeholder="Enter CMS ID"
              precision={0}
              min={0}
            />
          </Form.Item>
        </Col>
      </Row>

      <Row gutter={16}>
        <Col span={12}>
          <Form.Item
            name="type"
            label="Type"
            rules={[{ max: 100, message: 'Type must be less than 100 characters' }]}
          >
            <Input placeholder="Enter hospital type" />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item
            name="facility_type"
            label="Facility Type"
            rules={[{ max: 100, message: 'Facility type must be less than 100 characters' }]}
          >
            <Select
              placeholder="Select facility type"
              options={[
                { label: FACILITY_TYPES.HOSPITAL, value: FACILITY_TYPES.HOSPITAL },
                {
                  label: FACILITY_TYPES.MOBILE_ASSESSMENT,
                  value: FACILITY_TYPES.MOBILE_ASSESSMENT,
                },
                { label: FACILITY_TYPES.OTHER, value: FACILITY_TYPES.OTHER },
              ]}
              onChange={(value) => {
                // Clear the custom value when switching away from "Other"
                if (value !== FACILITY_TYPES.OTHER) {
                  form.setFieldValue('facility_type_custom', undefined)
                }
              }}
            />
          </Form.Item>
          <Form.Item
            noStyle
            shouldUpdate={(prevValues, currentValues) =>
              prevValues.facility_type !== currentValues.facility_type
            }
          >
            {() => {
              const facilityType = form.getFieldValue('facility_type')
              if (facilityType === FACILITY_TYPES.OTHER) {
                return (
                  <Form.Item
                    name="facility_type_custom"
                    rules={[
                      { required: true, message: 'Please enter a facility type' },
                      { max: 100, message: 'Facility type must be less than 100 characters' },
                    ]}
                    style={{ marginTop: 8 }}
                  >
                    <Input placeholder="Enter custom facility type" />
                  </Form.Item>
                )
              }
              return null
            }}
          </Form.Item>
        </Col>
      </Row>

      {/* Address Information */}
      <Divider orientation="left">Address</Divider>
      <Form.Item
        name="address"
        label="Street Address"
        rules={[{ max: 255, message: 'Address must be less than 255 characters' }]}
      >
        <Input placeholder="Enter street address" />
      </Form.Item>

      <Row gutter={16}>
        <Col span={8}>
          <Form.Item
            name="city"
            label="City"
            rules={[{ max: 100, message: 'City must be less than 100 characters' }]}
          >
            <Input placeholder="Enter city" />
          </Form.Item>
        </Col>
        <Col span={8}>
          <Form.Item
            name="state"
            label="State"
            rules={[{ len: 2, message: 'State must be a 2-letter code' }]}
          >
            <Select
              showSearch
              placeholder="Select state"
              optionFilterProp="children"
              filterOption={(input, option) =>
                (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
              }
              options={usStates.map((state) => ({ label: state, value: state }))}
            />
          </Form.Item>
        </Col>
        <Col span={8}>
          <Form.Item
            name="zip"
            label="ZIP Code"
            rules={[{ max: 20, message: 'ZIP code must be less than 20 characters' }]}
          >
            <Input placeholder="Enter ZIP code" />
          </Form.Item>
        </Col>
      </Row>

      <Row gutter={16}>
        <Col span={12}>
          <Form.Item
            name="county"
            label="County"
            rules={[{ max: 100, message: 'County must be less than 100 characters' }]}
          >
            <Input placeholder="Enter county" />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item
            name="phone"
            label="Phone Number"
            rules={[{ max: 50, message: 'Phone must be less than 50 characters' }]}
          >
            <Input placeholder="Enter phone number" />
          </Form.Item>
        </Col>
      </Row>

      {/* Location Coordinates */}
      <Divider orientation="left">Location Coordinates</Divider>
      <Row gutter={16}>
        <Col span={12}>
          <Form.Item
            name="lat"
            label="Latitude"
            rules={[
              { type: 'number', min: -90, max: 90, message: 'Latitude must be between -90 and 90' },
            ]}
          >
            <InputNumber
              style={{ width: '100%' }}
              placeholder="Enter latitude"
              precision={6}
              step={0.000001}
            />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item
            name="lon"
            label="Longitude"
            rules={[
              {
                type: 'number',
                min: -180,
                max: 180,
                message: 'Longitude must be between -180 and 180',
              },
            ]}
          >
            <InputNumber
              style={{ width: '100%' }}
              placeholder="Enter longitude"
              precision={6}
              step={0.000001}
            />
          </Form.Item>
        </Col>
      </Row>

      {/* Features & Flags */}
      <Divider orientation="left">Features & Capabilities</Divider>
      <Row gutter={[16, 16]}>
        <Col span={8}>
          <Form.Item name="participant" label="Participant" valuePropName="checked">
            <Switch />
          </Form.Item>
        </Col>
        <Col span={8}>
          <Form.Item name="intake" label="Intake" valuePropName="checked">
            <Switch />
          </Form.Item>
        </Col>
        <Col span={8}>
          <Form.Item name="receiver" label="Receiver" valuePropName="checked">
            <Switch />
          </Form.Item>
        </Col>
        <Col span={8}>
          <Form.Item name="is_transfer_center" label="Transfer Center" valuePropName="checked">
            <Switch />
          </Form.Item>
        </Col>
        <Col span={8}>
          <Form.Item name="critical_access" label="Critical Access" valuePropName="checked">
            <Switch />
          </Form.Item>
        </Col>
        <Col span={8}>
          <Form.Item name="emergency" label="Emergency" valuePropName="checked">
            <Switch />
          </Form.Item>
        </Col>
        <Col span={8}>
          <Form.Item name="cfr" label="CFR" valuePropName="checked">
            <Switch />
          </Form.Item>
        </Col>
      </Row>

      {/* Configuration */}
      <Divider orientation="left">Configuration</Divider>
      <Row gutter={16}>
        <Col span={8}>
          <Form.Item
            name="default_radius"
            label="Default Radius (miles)"
            rules={[
              { type: 'number', min: 0, max: 1000, message: 'Radius must be between 0 and 1000' },
            ]}
          >
            <InputNumber
              style={{ width: '100%' }}
              placeholder="Enter default radius"
              precision={0}
              min={0}
              max={1000}
            />
          </Form.Item>
        </Col>
        <Col span={8}>
          <Form.Item
            name="password_expiration"
            label="Password Expiration (days)"
            rules={[
              {
                type: 'number',
                min: 0,
                max: 365,
                message: 'Password expiration must be between 0 and 365',
              },
            ]}
          >
            <InputNumber
              style={{ width: '100%' }}
              placeholder="Enter days"
              precision={0}
              min={0}
              max={365}
            />
          </Form.Item>
        </Col>
        <Col span={8}>
          <Form.Item name="mfa_required" label="MFA Required" valuePropName="checked">
            <Switch />
          </Form.Item>
        </Col>
      </Row>

      <Row gutter={16}>
        <Col span={12}>
          <Form.Item name="market_id" label="Market">
            <Select
              showSearch
              allowClear
              placeholder={marketsLoading ? 'Loading markets...' : 'Select market'}
              loading={marketsLoading}
              notFoundContent={marketsLoading ? <Spin size="small" /> : 'No markets found'}
              optionFilterProp="children"
              filterOption={(input, option) =>
                (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
              }
              options={markets.map((market) => ({
                label: market.name,
                value: market.id,
              }))}
            />
          </Form.Item>
        </Col>
      </Row>

      {/* About */}
      <Divider orientation="left">About</Divider>
      <Form.Item
        name="about"
        label="Description"
        rules={[{ max: 5000, message: 'Description must be less than 5000 characters' }]}
      >
        <TextArea rows={4} placeholder="Enter hospital description" showCount maxLength={5000} />
      </Form.Item>
    </>
  )
}
